import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchThread, type CommentThread } from './comments';
import { fetchForKey, CACHE_UPDATE_EVENT } from './queryCache';

// =============================================================================
// useApiComments(anchorType, anchorId) — DEV-722
// Parameterized hook modeled on useApiOnboarding. Caches per anchor under
// "comments:{anchorType}:{anchorId}". One fetch hydrates the whole thread
// (mode + active reaction vocab + comments).
// =============================================================================

export interface UseApiCommentsResult {
  thread: CommentThread | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApiComments(
  anchorType: string | null | undefined,
  anchorId: string | null | undefined,
): UseApiCommentsResult {
  const [thread, setThread] = useState<CommentThread | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const cacheKey = anchorType && anchorId ? `comments:${anchorType}:${anchorId}` : null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (force: boolean) => {
      if (!cacheKey || !anchorType || !anchorId) return;
      try {
        const data = await fetchForKey(cacheKey, () => fetchThread(anchorType, anchorId), { force });
        if (!mountedRef.current) return;
        setThread(data);
        setLoading(false);
        setError(null);
      } catch (err) {
        if (!mountedRef.current) return;
        setLoading(false);
        setError(err instanceof Error ? err.message : 'Failed to load comments');
      }
    },
    [cacheKey, anchorType, anchorId],
  );

  const refetch = useCallback(() => {
    // Reload THIS thread locally. Deliberately does NOT dispatch the global
    // cache event: unrelated subscribers (e.g. the Role Digest's unfiltered
    // CACHE_UPDATE_EVENT -> fetchAll) would otherwise refetch on a comment
    // mutation and tear down the row hosting this open modal.
    load(true);
  }, [load]);

  useEffect(() => {
    setLoading(true);
    load(false);
  }, [load]);

  // Refetch when this anchor's cache key is invalidated (mutations, SignalR).
  useEffect(() => {
    if (!cacheKey) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === cacheKey) load(true);
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [cacheKey, load]);

  return { thread, loading, error, refetch };
}
